import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  cellRefusal,
  cellValueText,
  dragFillRect,
  rectHas,
  type CellPos,
  type CurrentMax,
  type Draft,
  type DraftLineOptions,
  type SlotDraft,
} from "@heraion/shared";

import { useLiveAnnouncement } from "../primitives/useLiveAnnouncement";
import type { CellFieldSpec } from "./RoundGridCell";
import { useGridClipboard } from "./useGridClipboard";
import { useGridKeys } from "./useGridKeys";
import { useGridRangeActions } from "./useGridRangeActions";
import { gridIds, type GridKeyHandlers } from "./gridKeyContract";
import { useGridSelection } from "./useGridSelection";
import { roundCellWrites, type GridWriteDeps } from "./useRoundCellWrites";
import type { V3Navigation } from "./useV3Navigation";

/** The characters that open the Sets field when typed on a cell: any other
 *  stays the browser's. */
const OPENS = /^\d$/;

// The wide grid's editing (#336 slice 6 part 2b): the selection, the open
// cell's field, the block writes, and the web's keys and mouse wired to them.
// A tap opens a cell on the right. In the Sets lens a tap on the open cell (or
// Enter, or a digit) opens its field; in the others the grid is read only and
// those just keep the exercise open on the right. For a finger, «Select» is a
// mode: the first tap anchors, the later ones grow the block and open nothing
// (the menu and the named actions are useGridRangeActions). Without `writes`
// (#358) a cell only opens: no field, no block, no copy, paste or fill.
export function useGridEditing({
  draft,
  nav,
  lineOptions,
  maxMeta,
  writes,
}: {
  draft: Draft;
  nav: V3Navigation;
  lineOptions: DraftLineOptions;
  maxMeta?: ReadonlyMap<string, CurrentMax>;
  /** Absent: read only. */
  writes?: GridWriteDeps;
}) {
  const { t } = useTranslation();
  const sIdx = nav.dayIdx;
  const lens = nav.gridLens;
  const slots = draft.sessions[sIdx]?.slots ?? [];
  const maxOf = (slot: SlotDraft) => maxMeta?.get(slot.exerciseId);
  const open = nav.openSlotIdx !== null && nav.expandedRound !== null ? { row: nav.openSlotIdx, col: nav.expandedRound } : null;
  const sel = useGridSelection({
    rows: slots.length,
    cols: draft.cycleWeeks,
    open,
    onOpen: (p) => {
      const slot = slots[p.row];
      if (slot) nav.openExerciseAt(slot.id, p.col);
    },
  });
  // This grid's own ids: another grid on the page has others (#358).
  const grid = useId().replace(/[^A-Za-z0-9]/g, "");
  const ids = gridIds(grid);
  // Read only: no writes to make, so none built.
  const cells = writes ? roundCellWrites(t, { ...writes, draft, sIdx, lineOptions, maxOf }) : null;
  const can = cells !== null;
  const [menuOpen, setMenuOpen] = useState(false);
  const clipboard = useGridClipboard();
  const range = useGridRangeActions({ t, lens, sel, cells, clipboard, showToast: writes?.showToast });
  const [fillTo, setFillTo] = useState<CellPos | null>(null);
  // The last cell the handle was over: the let-go may come before a render.
  const fillRef = useRef<CellPos | null>(null);
  const writable = (p: CellPos) => cellRefusal(slots[p.row], p.col, lens) === null;
  // A block that grows is said: its size changes nothing else on screen.
  const live = useLiveAnnouncement();
  const { speak } = live;
  const size = sel.rect ? (sel.rect.bottom - sel.rect.top + 1) * (sel.rect.right - sel.rect.left + 1) : 0;
  useEffect(() => {
    if (size > 1) speak(t("editorV3.grid.cellsSelected", { count: size, defaultValue: "{{count}} cells selected" }));
  }, [size, speak, t]);

  const handlers: GridKeyHandlers = {
    grid,
    active: open !== null,
    editing: sel.field !== null,
    anchorId: open ? ids.cell(open) : null,
    move: (dRow, dCol, extend) => sel.move(dRow, dCol, extend && can),
    tab: (back) => {
      const col = (open?.col ?? -1) + (back ? -1 : 1);
      if (!open || col < 0 || col >= draft.cycleWeeks) return false;
      sel.select({ row: open.row, col });
      return true;
    },
    edit: (seed) => {
      if (!open) return false;
      if (!can || lens !== "sets") {
        if (seed !== undefined) return false;
        const slot = slots[open.row];
        if (slot) nav.openExerciseAt(slot.id, open.col);
        return true;
      }
      if (!writable(open) || (seed !== undefined && !OPENS.test(seed))) return false;
      sel.edit(seed);
      return true;
    },
    collapse: () => {
      sel.collapse();
      sel.stopSelecting();
    },
    copy: () => (cells && sel.rect ? cells.copy(lens, sel.rect) : null),
    paste: (text) => {
      if (!cells) return false;
      if (sel.rect) cells.paste(lens, sel.rect, text);
      return true;
    },
    fill: (dir) => {
      if (!cells) return false;
      if (sel.rect) cells.fill(lens, sel.rect, dir);
      return true;
    },
    extendTo: can ? sel.extendTo : sel.select,
    dragFrom: (from, to) => {
      if (can) sel.dragFrom(from, to);
    },
    // The context key or a right click: the block's actions, on the cell's own
    // block, or the cell if it is not in one.
    contextMenu: (p) => {
      if (!can) return false;
      if (!sel.rect || !rectHas(sel.rect, p)) sel.select(p);
      setMenuOpen(true);
      return true;
    },
    fillOver: (p) => {
      fillRef.current = p;
      setFillTo(p);
    },
    fillEnd: () => {
      const to = fillRef.current;
      fillRef.current = null;
      setFillTo(null);
      const src = sel.rect;
      const dest = src && to ? dragFillRect(src, to) : null;
      if (!cells || !src || !open || !to || !dest) return;
      cells.dragFill(lens, src, to);
      // The selection becomes the filled block, from the corner of it on the
      // open cell's side, so a block started from below covers it all too.
      const from = { row: open.row === src.top ? dest.top : dest.bottom, col: open.col === src.left ? dest.left : dest.right };
      sel.dragFrom(from, { row: from.row === dest.top ? dest.bottom : dest.top, col: from.col === dest.left ? dest.right : dest.left });
    },
  };
  const keys = useGridKeys(handlers);

  const rect = sel.rect;
  return {
    keys,
    /** The DOM ids of this grid's cells and fill handle. */
    ids,
    /** The live region that says the block's size: the grid mounts it. */
    region: live.region,
    open,
    range: rect && fillTo ? (dragFillRect(rect, fillTo) ?? rect) : rect,
    handleAt: can && keys.hasKeys && lens === "sets" && rect && !sel.field && !sel.selecting ? { row: rect.bottom, col: rect.right } : null,
    /** The «Select» button's state, and its toggle. */
    selecting: sel.selecting,
    toggleSelecting: () => (sel.selecting ? handlers.collapse() : sel.startSelecting()),
    /** The range bar's props: in the mode, or with more than one cell selected. */
    rangeBar:
      can && (sel.selecting || size > 1 || menuOpen)
        ? { count: size, items: range.menuItems, menuOpen, setMenuOpen, onDone: handlers.collapse }
        : null,
    /** A cell's hold and named actions. */
    cellGesture: range.cellGesture,
    onCell: (p: CellPos) => {
      if (sel.selecting) return sel.tap(p);
      const again = !!open && open.row === p.row && open.col === p.col && sel.single;
      if (!again) sel.select(p);
      else handlers.edit();
    },
    fieldAt: (p: CellPos, name: string, round: string): CellFieldSpec | undefined => {
      if (!cells || !sel.field || lens !== "sets") return undefined;
      const slot = slots[p.row];
      return {
        value: cellValueText(slot, p.col, lens, lineOptions, slot && maxOf(slot)),
        seed: sel.field.seed,
        a11yLabel: t("editorV3.grid.setsFieldA11y", { name, round, defaultValue: "{{name}}, {{round}}: sets in this round" }),
        onCommit: (text) => cells.writeOne(p, text),
        onClose: sel.stopEdit,
        onTab: (back) => handlers.tab(back),
      };
    },
  };
}
